import React, { useState, useEffect } from 'react';
import { downloadFile } from '../../services/fileApi';

const SecureImage = ({ src, alt, className, fallback, onClick, zoomable = true }) => {
  const [imgSrc, setImgSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);

  useEffect(() => {
    if (!src) {
      setImgSrc(null);
      return;
    }

    if (src.startsWith('data:') || src.startsWith('blob:') || src.startsWith('http')) {
      setImgSrc(src);
      return;
    }

    // Extract fileId from path like "/api/files/download/fileId"
    const parts = src.split('/');
    const fileId = parts[parts.length - 1];

    if (!fileId) {
      setImgSrc(null);
      return;
    }

    let isMounted = true;
    setLoading(true);

    const fetchImage = async () => {
      try {
        const response = await downloadFile(fileId);
        if (isMounted) {
          const blob = new Blob([response.data], { type: response.headers['content-type'] });
          const blobUrl = URL.createObjectURL(blob);
          setImgSrc(blobUrl);
        }
      } catch (err) {
        console.error('Failed to load secure image:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchImage();

    return () => {
      isMounted = false;
      if (imgSrc && imgSrc.startsWith('blob:')) {
        URL.revokeObjectURL(imgSrc);
      }
    };
  }, [src]);

  if (loading) {
    return (
      <div className={`${className} flex items-center justify-center bg-gray-150`}>
        <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!imgSrc) {
    return fallback || null;
  }

  const handleImageClick = (e) => {
    if (onClick) {
      onClick(e);
    }
    if (zoomable) {
      setIsZoomed(!isZoomed);
    }
  };

  return (
    <>
      <img 
        src={imgSrc} 
        alt={alt} 
        className={`${className} ${zoomable ? 'cursor-zoom-in hover:scale-105 active:scale-95 transition-all duration-300' : ''}`} 
        onClick={handleImageClick} 
      />
      {isZoomed && (
        <div 
          className="fixed inset-0 bg-slate-900/80 backdrop-blur-xs z-[9999] cursor-zoom-out animate-in fade-in duration-200 flex items-center justify-center p-4"
          onClick={() => setIsZoomed(false)}
        >
          <img
            src={imgSrc}
            alt={alt}
            className="w-[300px] h-[300px] sm:w-[400px] sm:h-[400px] md:w-[500px] md:h-[500px] rounded-3xl shadow-2xl object-cover animate-in zoom-in-95 duration-200 cursor-zoom-out"
          />
        </div>
      )}
    </>
  );
};

export default SecureImage;
